import { getStorageInfo } from '../api/systems/api'
import { IResponseStorageInfo } from '../api/systems/api.types'

import { getStorageWarnings, IStorageWarning } from './storage-health.service'


export const STORAGE_HEALTH_CHECK_INTERVAL_MS = 60 * 60 * 1000

type StorageInfoLoader = (signal?: AbortSignal) => Promise<IResponseStorageInfo>
type StorageWarningsListener = (warnings: IStorageWarning[]) => void

export class StorageHealthMonitorService {
    private readonly listeners = new Set<StorageWarningsListener>()
    private interval: ReturnType<typeof setInterval> | null = null
    private abortController: AbortController | null = null
    private isCheckInProgress = false
    private generation = 0

    constructor(
        private readonly loadStorageInfo: StorageInfoLoader = (signal) => getStorageInfo(signal, true),
    ) {}

    subscribe(listener: StorageWarningsListener) {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    start() {
        if (this.interval) return Promise.resolve()

        const initialCheck = this.checkNow()
        this.interval = setInterval(() => {
            void this.checkNow()
        }, STORAGE_HEALTH_CHECK_INTERVAL_MS)

        return initialCheck
    }

    stop() {
        if (this.interval) {
            clearInterval(this.interval)
            this.interval = null
        }

        this.generation += 1
        this.abortController?.abort()
        this.abortController = null
        this.isCheckInProgress = false
    }

    async checkNow() {
        if (this.isCheckInProgress) return

        this.isCheckInProgress = true
        const generation = this.generation
        const abortController = new AbortController()
        this.abortController = abortController

        try {
            const response = await this.loadStorageInfo(abortController.signal)

            if (
                generation !== this.generation
                || response.status !== 200
                || !response.data
            ) return

            const warnings = getStorageWarnings(response.data)
            this.listeners.forEach((listener) => listener(warnings))
        } catch {
            return
        } finally {
            if (this.abortController === abortController) {
                this.abortController = null
                this.isCheckInProgress = false
            }
        }
    }
}

const storageHealthMonitorService = new StorageHealthMonitorService()

export default storageHealthMonitorService